import { useField } from 'formik'
import { useDebouncedCallback } from 'use-debounce'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import {
  createSpecialityOption,
  getSpecialityOptions,
  latestCreatedSpecialityOptionIdSelector,
  selectSpecialityOptionCreateRequestTracker,
  selectSpecialityOptions,
} from '@/redux/reducers/speciality-option.reducer'

import SpecialitiesAutoComplete, { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete'

type SpecialitiesAutocompleteFieldProps = {
  id: string
  name: string
  label: string
  editable: boolean
  canDelete?: boolean
}

export default function SpecialitiesAutocompleteField({ id, name, label, editable, canDelete }: SpecialitiesAutocompleteFieldProps) {
  const dispatch = useAppDispatch()

  const specialities = useAppSelector(selectSpecialityOptions)

  const [{ value: selectedSpecialities }, , { setValue: setSelectedSpecialities }] = useField<SpecialityAutocompleteOption[]>(name)

  const latestCreatedSpeciality = useAppSelector(latestCreatedSpecialityOptionIdSelector)

  const { dispatch: createSpecialityOptionDispatch } = useRequestHandler({
    onSuccess: () => {
      // Reload the speciality options after successful creation
      dispatch(getSpecialityOptions({}))
      // Add the newly created speciality to the formik field
      setSelectedSpecialities([
        ...selectedSpecialities,
        specialities.find((speciality) => speciality._id === latestCreatedSpeciality) as SpecialityAutocompleteOption,
      ])
    },
    onError: () => { },
    requestTrackerSelector: selectSpecialityOptionCreateRequestTracker,
  })

  const handleInputChange = useDebouncedCallback((newInputValue: string) => {
    dispatch(getSpecialityOptions({ smartSearch: newInputValue }))
  }, 500)

  const handleSpecialityChange = (event: any, newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null, reason: string) => {
    if (typeof newValue === 'string' || !Array.isArray(newValue)) {
      return
    }
    if (reason === 'removeOption') {
      // Remove the speciality from the formik field
      setSelectedSpecialities(newValue as SpecialityAutocompleteOption[])
      return
    }
    if (reason === 'createOption') {
      // find the string in the newValue array
      const speciality = newValue.find((speciality) => typeof speciality === 'string')
      if (!speciality) {
        return
      }
      if (!(typeof speciality === 'string')) {
        return
      }
      createSpecialityOptionDispatch(
        createSpecialityOption({
          name: speciality,
          code: 'CREATED.' + speciality.toLowerCase().replace(/ /g, '-'),
        })
      )
      return
    }
    if (reason === 'selectOption') {
      // check wether added option contains "Add" in it
      const speciality = newValue.find((speciality) => typeof speciality !== 'string' && speciality.isNew)
      if (speciality && typeof speciality !== 'string') {
        if (!speciality.inputValue) {
          return
        }
        createSpecialityOptionDispatch(
          createSpecialityOption({
            name: (speciality as SpecialityAutocompleteOption).inputValue as string,
            code: 'CREATED.' + ((speciality as SpecialityAutocompleteOption).inputValue as string).toLowerCase().replace(/ /g, '-'),
          })
        )
        return
      }
    }
    setSelectedSpecialities(newValue as SpecialityAutocompleteOption[])
  }

  return (
    <SpecialitiesAutoComplete
      id={id}
      name={name}
      label={label}
      editable={editable}
      canDelete={canDelete}
      selectedSpecialities={selectedSpecialities}
      specialities={specialities}
      onInputChange={handleInputChange}
      onSpecialityChange={handleSpecialityChange}
    />
  )
}
